import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface WebSocketMessage {
  type: string;
  data: any;
}

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onCampaignUpdate?: (campaign: any) => void;
  onCampaignCreated?: (campaign: any) => void;
  onCampaignStatusChange?: (campaignId: string, status: string) => void;
  reconnectInterval?: number;
  enabled?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const {
    onMessage,
    onCampaignUpdate,
    onCampaignCreated,
    onCampaignStatusChange,
    reconnectInterval = 3000,
    enabled = true,
  } = options;

  const { accessToken } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef<boolean>(true);
  const lastTokenRef = useRef<string | null>(null);
  const mountTimeRef = useRef<number>(Date.now());

  const connect = useCallback(() => {
    if (!accessToken || !enabled) {
      logger.log('[WebSocket] Não conectando - accessToken ou enabled é falso');
      return;
    }

    // Se já existe uma conexão OPEN com o mesmo token, não reconectar
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && lastTokenRef.current === accessToken) {
      logger.log('[WebSocket] Já conectado com o mesmo token - ignorando reconexão');
      return;
    }

    // Fechar conexão existente se houver
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        logger.log('[WebSocket] Fechando conexão existente antes de criar nova');
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // Armazenar o token atual
    lastTokenRef.current = accessToken;

    try {
      // Construir URL do WebSocket
      const wsUrl = `${import.meta.env.VITE_WS_URL || 'ws://localhost:3001'}/ws?token=${accessToken}`;

      logger.log('[WebSocket] Conectando com token:', accessToken.substring(0, 20) + '...');

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        logger.log('[WebSocket] Conectado com sucesso');
        setIsConnected(true);

        // Enviar autenticação se necessário
        ws.send(JSON.stringify({
          type: 'auth',
          token: accessToken,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          logger.debug(`[WebSocket] Evento Recebido: ${message.type}`, message.data);

          setLastMessage(message);

          // Callback genérico
          if (onMessage) {
            logger.log(`[WebSocket] Chamando callback onMessage para tipo: ${message.type}`);
            onMessage(message);
          }

          // Callbacks específicos
          switch (message.type) {
            case 'connection:established':
              logger.log('[WebSocket] Conexão estabelecida pelo servidor', message.data);
              break;

            case 'campaign:created':
              logger.log('[WebSocket] Campanha criada:', message.data?.id, message.data?.name);
              if (onCampaignCreated) {
                onCampaignCreated(message.data);
              }
              break;

            case 'campaign:updated':
              logger.log('[WebSocket] Campanha atualizada:', message.data?.id);
              if (onCampaignUpdate) {
                onCampaignUpdate(message.data);
              }
              break;

            case 'campaign:status':
              logger.log('[WebSocket] Status da campanha:', message.data?.campaignId, message.data?.status);
              if (onCampaignStatusChange) {
                onCampaignStatusChange(message.data.campaignId, message.data.status);
              }
              break;

            case 'campaign:progress':
              logger.log('[WebSocket] Progresso da campanha:', message.data?.id || message.data?.campaignId,
                `${message.data?.sentCount || 0}/${message.data?.totalRecipients || 0}`);
              if (onCampaignUpdate) {
                onCampaignUpdate(message.data);
              }
              break;

            default:
              logger.warn('[WebSocket] Tipo de mensagem desconhecido:', message.type, message.data);
          }
        } catch (error) {
          logger.error('[WebSocket] Erro ao processar mensagem:', error);
        }
      };

      ws.onerror = (error) => {
        logger.error('[WebSocket] Erro:', error);
        setIsConnected(false);
      };

      ws.onclose = () => {
        logger.log('[WebSocket] Desconectado');
        setIsConnected(false);

        // Só limpar ref se for ESTA conexão
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        // Detectar se foi fechado muito rápido após montagem (provável remontagem do StrictMode)
        const timeSinceMount = Date.now() - mountTimeRef.current;
        const wasQuickRemount = timeSinceMount < 100;

        // Só tentar reconectar se TODAS as condições forem verdadeiras
        if (enabled && accessToken && shouldReconnectRef.current) {
          // Se foi uma remontagem rápida (StrictMode), esperar mais tempo antes de reconectar
          const delay = wasQuickRemount ? reconnectInterval * 2 : reconnectInterval;

          if (wasQuickRemount) {
            logger.log('[WebSocket] Remontagem rápida detectada - aguardando', delay, 'ms antes de reconectar');
          } else {
            logger.log('[WebSocket] Agendando reconexão em', delay, 'ms');
          }

          reconnectTimeoutRef.current = setTimeout(() => {
            if (accessToken && shouldReconnectRef.current) {
              logger.log('[WebSocket] Tentando reconectar...');
              connect();
            } else {
              logger.log('[WebSocket] Cancelando reconexão - sem accessToken ou shouldReconnect=false');
            }
          }, delay);
        } else {
          logger.log('[WebSocket] Não reconectando - enabled:', enabled, 'accessToken:', !!accessToken, 'shouldReconnect:', shouldReconnectRef.current);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      logger.error('[WebSocket] Erro ao conectar:', error);
      setIsConnected(false);
    }
  }, [accessToken, enabled, onMessage, onCampaignUpdate, onCampaignCreated, onCampaignStatusChange, reconnectInterval]);

  const disconnect = useCallback(() => {
    logger.log('[WebSocket] disconnect() chamado - parando reconexões');
    shouldReconnectRef.current = false;
    lastTokenRef.current = null;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
      logger.log('[WebSocket] Timeout de reconexão cancelado');
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      logger.log('[WebSocket] Conexão fechada');
    }

    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      logger.warn('[WebSocket] Não conectado. Mensagem não enviada:', message);
    }
  }, []);

  // Conectar/desconectar e reconectar quando token muda
  useEffect(() => {
    logger.log('[WebSocket] useEffect triggered - enabled:', enabled, 'accessToken:', !!accessToken);

    // Atualizar tempo de montagem a cada nova montagem
    mountTimeRef.current = Date.now();

    if (enabled && accessToken) {
      logger.log('[WebSocket] Token detectado - iniciando/reconectando WebSocket');
      shouldReconnectRef.current = true; // Permitir reconexões

      // connect() vai lidar com o fechamento de conexões existentes
      connect();
    } else {
      logger.log('[WebSocket] Sem token ou disabled - desconectando');
      disconnect();
    }

    return () => {
      logger.log('[WebSocket] Cleanup - desmontando componente');
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, accessToken]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    connect,
    disconnect,
  };
}
